/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm, CRow, CCol, CFormInput, CFormLabel, CButton, CSpinner, CAlert, CFormSelect
} from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';
import { getStockDropdown } from '../../services/endpoints';
import '../OrderInputs/OrderInputs.scss';
import { toScaledIntegerStringFromParts } from '../../helpers/quantity';
import SearchableSelect from '../SearchableSelect/SearchableSelect';

const StockInputs = ({
  onSubmit,
  onCancel,
  isLoading = false,
  submitResponse = null,
  initialData = null,
  isEdit = false
}) => {
  const [formData, setFormData] = useState({
    PrdUnitGuid: '',
    OpsStockStatusGuid: '',
    ProductStockQuantity: '',
    RollCount: '',
    StockPlace: '',
    Lot: '',
    Description: '',
    OrderGuid: ''
  });
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();
  const [unitOptions, setUnitOptions] = useState([]);
  const [stockStatusOptions, setStockStatusOptions] = useState([]);
  const [orderOptions, setOrderOptions] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(true);
  const [dropdownError, setDropdownError] = useState(null);

  // Separate decimal inputs for stock quantity
  const [stockQuantityInputs, setStockQuantityInputs] = useState({
    stockWhole: '',
    stockDecimal: ''
  });

  const splitFromDisplayQuantity = (raw) => {
    if (raw === null || raw === undefined) {
      return { whole: '', decimal: '' };
    }

    const normalized = String(raw).trim().replace(',', '.');
    if (!normalized) {
      return { whole: '', decimal: '' };
    }

    const numericValue = Number(normalized);
    if (Number.isNaN(numericValue)) {
      return { whole: '', decimal: '' };
    }

    const fixed = numericValue.toFixed(2);
    const [whole, decimal] = fixed.split('.');
    return { whole, decimal };
  };

  // splitting handled by helpers

  const fetchOrderOptionsByOrderNo = async (searchTerm) => {
    const sessionUserId = localStorage.getItem('sessionUserId');
    const trimmedSearchTerm = (searchTerm || '').trim();

    if (!trimmedSearchTerm) {
      setOrderOptions([]);
      return;
    }

    try {
      const response = await getStockDropdown({
        SessionUserId: sessionUserId,
        OrderNo: trimmedSearchTerm
      });
      const data = response?.data || {};
      setOrderOptions(data.Orders || []);
    } catch (err) {
      setOrderOptions([]);
    }
  };

  useEffect(() => {
    const fetchDropdown = async () => {
      setDropdownLoading(true);
      setDropdownError(null);
      try {
        const sessionUserId = localStorage.getItem('sessionUserId');
        const response = await getStockDropdown({ SessionUserId: sessionUserId });
        const data = response?.data || {};
        setUnitOptions(data.Units || []);
        setStockStatusOptions(data.StockStatuses || []);
        setOrderOptions([]);
      } catch (err) {
        setDropdownError('Dropdown verileri alınamadı.');
        setUnitOptions([]);
        setStockStatusOptions([]);
        setOrderOptions([]);
      } finally {
        setDropdownLoading(false);
      }
    };
    fetchDropdown();
  }, []);

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      const orderGuid = String(initialData.OpsOrderGuid || initialData.OrderGuid || '');
      
      const newFormData = {
        PrdUnitGuid: initialData.PrdUnitGuid || '',
        OpsStockStatusGuid: initialData.OpsStockStatusGuid || '',
        ProductStockQuantity: initialData.ProductStockQuantity || '',
        RollCount: initialData.RollCount ?? '',
        StockPlace: initialData.StockPlace || '',
        Lot: initialData.Lot || '',
        Description: initialData.Description || '',
        OrderGuid: orderGuid
      };
      
      setFormData(newFormData);

      // Initialize split inputs from initial ProductStockQuantity (display value aware)
      if (initialData.ProductStockQuantity !== undefined && initialData.ProductStockQuantity !== null && initialData.ProductStockQuantity !== '') {
        const { whole, decimal } = splitFromDisplayQuantity(initialData.ProductStockQuantity);
        setStockQuantityInputs({ stockWhole: whole, stockDecimal: decimal });
      } else {
        setStockQuantityInputs({ stockWhole: '', stockDecimal: '' });
      }
    }
  }, [initialData]);

  // Keep formData.ProductStockQuantity in sync with split inputs
  useEffect(() => {
    const hasAny = stockQuantityInputs.stockWhole !== '' || stockQuantityInputs.stockDecimal !== '';
    const combined = hasAny
      ? `${stockQuantityInputs.stockWhole || '0'}.${(stockQuantityInputs.stockDecimal || '00').padEnd(2, '0').slice(0, 2)}`
      : '';
    setFormData(prev => ({ ...prev, ProductStockQuantity: combined }));
  }, [stockQuantityInputs.stockWhole, stockQuantityInputs.stockDecimal]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setTimeout(() => {
        if (!isEdit) {
          setFormData({
            PrdUnitGuid: '',
            OpsStockStatusGuid: '',
            ProductStockQuantity: '',
            RollCount: '',
            StockPlace: '',
            Lot: '',
            Description: '',
            OrderGuid: ''
          });
          setStockQuantityInputs({ stockWhole: '', stockDecimal: '' });
        }
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleStockQuantityInputChange = (part, value) => {
    if (part === 'whole') {
      if (!/^\d*$/.test(value)) return;
    }
    if (part === 'decimal') {
      if (!/^\d{0,2}$/.test(value)) return;
    }
    setStockQuantityInputs(prev => ({ ...prev, [part === 'whole' ? 'stockWhole' : 'stockDecimal']: value }));
    if (errors.ProductStockQuantity) {
      setErrors(prev => ({ ...prev, ProductStockQuantity: '' }));
    }
  };

  const handleStockQuantityKeyDown = (e, part) => {
    if (part === 'whole') {
      const isDotOrCommaKey = e.key === '.' || e.key === ',' || e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
      if (isDotOrCommaKey) {
        e.preventDefault();
        const decimalInput = document.getElementById('stockQuantityDecimal');
        if (decimalInput) {
          decimalInput.focus();
          const valueLength = decimalInput.value ? decimalInput.value.length : 0;
          try { decimalInput.setSelectionRange(valueLength, valueLength); } catch (err) {}
        }
        return;
      }
    }
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    if (part === 'decimal') {
      const currentValue = e.target.value;
      const selectionStart = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      if (currentValue.length >= 2 && selectionStart === selectionEnd) {
        e.preventDefault();
        return;
      }
    }
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const handleStockQuantityFocus = (part) => {
    if (part === 'whole') {
      const input = document.getElementById('stockQuantityWhole');
      if (input && input.value) {
        setTimeout(() => input.select(), 0);
      }
    }
  };

  const handleStockQuantityBlur = (part) => {
    if (part === 'decimal') {
      const currentValue = stockQuantityInputs.stockDecimal;
      if (currentValue !== '' && currentValue.length === 1) {
        setStockQuantityInputs(prev => ({ ...prev, stockDecimal: currentValue.padEnd(2, '0') }));
      } else if (currentValue === '' && stockQuantityInputs.stockWhole) {
        setStockQuantityInputs(prev => ({ ...prev, stockDecimal: '00' }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.OrderGuid || !String(formData.OrderGuid).trim()) {
      newErrors.OrderGuid = t('orderGuidRequired') || 'Order is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;
      if (!userId || userId === 'undefined') {
        setErrors({ general: 'User session not found. Please login again.' });
        return;
      }
      const submitData = {
        SessionUserId: String(userId),
        PrdUnitGuid: String(formData.PrdUnitGuid || '').trim(),
        OpsStockStatusGuid: String(formData.OpsStockStatusGuid || '').trim(),
        ProductStockQuantity: toScaledIntegerStringFromParts(stockQuantityInputs.stockWhole, stockQuantityInputs.stockDecimal),
        RollCount: String(formData.RollCount || '').trim(),
        StockPlace: String(formData.StockPlace || '').trim(),
        Lot: String(formData.Lot || '').trim(),
        Description: String(formData.Description || '').trim(),
        OpsOrderGuid: String(formData.OrderGuid || '').trim()
      };
      if (isEdit && initialData?.StockGuid) {
        submitData.StockId = initialData.StockGuid;
      }
      onSubmit(submitData);
    }
  };

  const handleCancel = () => {
    if (!isEdit) {
      setFormData({
        PrdUnitGuid: '',
        OpsStockStatusGuid: '',
        ProductStockQuantity: '',
        RollCount: '',
        StockPlace: '',
        Lot: '',
        Description: '',
        OrderGuid: ''
      });
    }
    setErrors({});
    onCancel();
  };

  const getButtonText = () => {
    if (isLoading) {
      return isEdit ? t('updating') : t('creating');
    }
    return isEdit ? t('updateStock') || 'Update Stock' : t('createStock') || 'Create Stock';
  };

  return (
    <div className="order-inputs-container">
      {errors.general && (
        <CAlert color="danger" dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}
      {submitResponse?.Result?.ErrorCode !== 0 && submitResponse?.Result?.ErrorDescription && (
        <CAlert color="danger">{submitResponse.Result.ErrorDescription}</CAlert>
      )}
      <CForm onSubmit={handleSubmit} noValidate>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="OrderGuid">{t('order') || 'Order Guid'}</CFormLabel>
            <SearchableSelect
              id="OrderGuid"
              name="OrderGuid"
              options={orderOptions.map(item => ({
                ...item,
                OpsOrderGuid: String(item.OpsOrderGuid || ''),
                Name: [item.OrderNo, item.OpsCustomerName, item.OpsSupplierName, item.WeaveCode, item.YarnName, item.YarnCode].filter(Boolean).join(' - ')
              }))}
              value={formData.OrderGuid}
              onChange={(selectedValue) => handleInputChange({ target: { name: 'OrderGuid', value: selectedValue } })}
              placeholder={dropdownLoading ? t('loading') : t('select')}
              disabled={isLoading || dropdownLoading}
              invalid={!!errors.OrderGuid}
              displayKey="Name"
              valueKey="OpsOrderGuid"
              emptyText={t('No options found')}
              onSearchTermChange={fetchOrderOptionsByOrderNo}
            />
            {errors.OrderGuid && <div className="invalid-feedback d-block">{errors.OrderGuid}</div>}
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor="OpsStockStatusGuid">{t('opsStockStatus') || 'Stock Status'}</CFormLabel>
            <CFormSelect
              id="OpsStockStatusGuid"
              name="OpsStockStatusGuid"
              value={formData.OpsStockStatusGuid}
              onChange={handleInputChange}
              invalid={!!errors.OpsStockStatusGuid}
              disabled={isLoading || dropdownLoading}
            >
              <option value="">{dropdownLoading ? t('loading') : t('select')}</option>
              {stockStatusOptions.map((item) => (
                <option key={item.OpsStockStatusGuid} value={item.OpsStockStatusGuid}>
                  {item.StockStatusName}
                </option>
              ))}
            </CFormSelect>
            {errors.OpsStockStatusGuid && <div className="invalid-feedback d-block">{errors.OpsStockStatusGuid}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="ProductStockQuantity">{t('productStockQuantity') || 'Quantity'}</CFormLabel>
            <div className={`price-input-group ${errors.ProductStockQuantity ? 'is-invalid' : ''}`}>
              <div className="price-input-section">
                <CFormInput
                  type="text"
                  id="stockQuantityWhole"
                  className="price-whole"
                  value={stockQuantityInputs.stockWhole}
                  onChange={(e) => handleStockQuantityInputChange('whole', e.target.value)}
                  onKeyDown={(e) => handleStockQuantityKeyDown(e, 'whole')}
                  onFocus={() => handleStockQuantityFocus('whole')}
                  placeholder="0"
                  disabled={isLoading}
                />
                <span className="decimal-separator">,</span>
                <CFormInput
                  type="text"
                  id="stockQuantityDecimal"
                  className="price-decimal"
                  value={stockQuantityInputs.stockDecimal}
                  onChange={(e) => handleStockQuantityInputChange('decimal', e.target.value)}
                  onKeyDown={(e) => handleStockQuantityKeyDown(e, 'decimal')}
                  onFocus={() => handleStockQuantityFocus('decimal')}
                  onBlur={() => handleStockQuantityBlur('decimal')}
                  placeholder="00"
                  maxLength="2"
                  disabled={isLoading}
                />
              </div>
            </div>
            {errors.ProductStockQuantity && <div className="invalid-feedback d-block">{errors.ProductStockQuantity}</div>}
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor="PrdUnitGuid">{t('prdUnit') || 'PrdUnit'}</CFormLabel>
            <CFormSelect
              id="PrdUnitGuid"
              name="PrdUnitGuid"
              value={formData.PrdUnitGuid}
              onChange={handleInputChange}
              invalid={!!errors.PrdUnitGuid}
              disabled={isLoading || dropdownLoading}
            >
              <option value="">{dropdownLoading ? t('loading') : t('select')}</option>
              {unitOptions.map((item) => (
                <option key={item.PrdUnitGuid} value={item.PrdUnitGuid}>
                  {item.UnitName}
                </option>
              ))}
            </CFormSelect>
            {errors.PrdUnitGuid && <div className="invalid-feedback d-block">{errors.PrdUnitGuid}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="StockPlace">{t('stockPlace') || 'Stock Place'}</CFormLabel>
            <CFormInput
              type="text"
              id="StockPlace"
              name="StockPlace"
              value={formData.StockPlace}
              onChange={handleInputChange}
              invalid={!!errors.StockPlace}
              placeholder={t('stockPlace') || 'Stock Place'}
              disabled={isLoading}
            />
            {errors.StockPlace && <div className="invalid-feedback d-block">{errors.StockPlace}</div>}
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor="Lot">{t('lot') || 'Lot'}</CFormLabel>
            <CFormInput
              type="text"
              id="Lot"
              name="Lot"
              value={formData.Lot}
              onChange={handleInputChange}
              invalid={!!errors.Lot}
              placeholder={t('lot') || 'Lot'}
              disabled={isLoading}
            />
            {errors.Lot && <div className="invalid-feedback d-block">{errors.Lot}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="RollCount">{t('stockRollCount') || 'Roll Count'}</CFormLabel>
            <CFormInput
              type="number"
              min="0"
              step="1"
              id="RollCount"
              name="RollCount"
              value={formData.RollCount}
              onChange={handleInputChange}
              invalid={!!errors.RollCount}
              placeholder={t('enterStockRollCount')}
              disabled={isLoading}
            />
            {errors.RollCount && <div className="invalid-feedback d-block">{errors.RollCount}</div>}
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor="Description">{t('description')}</CFormLabel>
            <CFormInput
              type="text"
              id="Description"
              name="Description"
              value={formData.Description}
              onChange={handleInputChange}
              invalid={!!errors.Description}
              placeholder={t('description')}
              disabled={isLoading}
            />
            {errors.Description && <div className="invalid-feedback d-block">{errors.Description}</div>}
          </CCol>
        </CRow>
        <CRow>
          <CCol md={12} className="d-flex gap-2">
            <CButton color="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  {getButtonText()}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  {getButtonText()}
                </>
              )}
            </CButton>
            <CButton color="secondary" variant="outline" onClick={handleCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" />
              {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

StockInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default StockInputs; 